import { useAuth } from "@/hooks/use-auth";

export const SUPPORTED_CURRENCIES = [
  { code: "GBP", label: "British Pound (£)" },
  { code: "USD", label: "US Dollar ($)" },
  { code: "EUR", label: "Euro (€)" },
  { code: "AUD", label: "Australian Dollar (A$)" },
  { code: "CAD", label: "Canadian Dollar (C$)" },
  { code: "NZD", label: "New Zealand Dollar (NZ$)" },
] as const;

export type CurrencyCode = (typeof SUPPORTED_CURRENCIES)[number]["code"];

export const DEFAULT_CURRENCY: CurrencyCode = "GBP";

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(currency: string, opts?: Intl.NumberFormatOptions) {
  const key = `${currency}|${JSON.stringify(opts ?? {})}`;
  let f = formatterCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      ...opts,
    });
    formatterCache.set(key, f);
  }
  return f;
}

export function formatMoney(
  amount: number | string | null | undefined,
  currency: string = DEFAULT_CURRENCY,
  opts?: Intl.NumberFormatOptions,
): string {
  const n = typeof amount === "string" ? parseFloat(amount) : (amount ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  return getFormatter(currency, opts).format(safe);
}

/** Format a per-metre rate, e.g. "£12.00/m" */
export function formatPerMetre(
  amount: number | string | null | undefined,
  currency: string = DEFAULT_CURRENCY,
): string {
  return `${formatMoney(amount, currency)}/m`;
}

/** Hook returning the current user's preferred currency (falls back to GBP). */
export function useCurrency(): CurrencyCode {
  const { profile } = useAuth();
  const code = (profile?.currency ?? DEFAULT_CURRENCY) as CurrencyCode;
  return SUPPORTED_CURRENCIES.some((c) => c.code === code) ? code : DEFAULT_CURRENCY;
}

/** Hook returning a bound formatter using the user's currency. */
export function useFormatMoney() {
  const currency = useCurrency();
  return (amount: number | string | null | undefined, opts?: Intl.NumberFormatOptions) =>
    formatMoney(amount, currency, opts);
}
